import { Stack, Typography, Chip, Link as MuiLink } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { palette } from '../../theme';
import { dictionary } from '../../dictionary';
import SectionHeading from '../shared/SectionHeading';
import type { TeamMember } from './TeamMemberDrawer.types';

interface TeamMemberDrawerContentProps {
  member: TeamMember;
  onClose: () => void;
}

export default function TeamMemberDrawerContent({ member, onClose }: TeamMemberDrawerContentProps) {
  const drawerDict = dictionary.about.team.drawer;

  return (
    <Stack
      gap={4}
      sx={{
        px: { mobile: 3, laptop: 8 },
        py: { mobile: 4, laptop: 6 },
        maxWidth: 800,
        mx: 'auto',
        width: '100%',
      }}
    >
      {/* Specializations */}
      {member.specializations.length > 0 && (
        <Stack gap={2}>
          <SectionHeading title={drawerDict.specializations} barColor={palette.secondary} />
          <Stack direction='row' gap={1} flexWrap='wrap' useFlexGap sx={{ pr: 2.5 }}>
            {member.specializations.map((spec) => (
              <Chip
                key={spec}
                label={spec}
                sx={{
                  backgroundColor: palette.backgroundSections,
                  color: palette.primary,
                  border: `1px solid ${palette.border}`,
                  fontSize: '0.875rem',
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {/* About */}
      <Stack gap={2}>
        <SectionHeading title={drawerDict.about} barColor={palette.accent} />
        <Typography variant='body1' sx={{ lineHeight: 1.9, pr: 2.5 }}>
          {member.about}
        </Typography>
      </Stack>

      {/* Education */}
      <Stack gap={2}>
        <SectionHeading title={drawerDict.education} barColor={palette.secondary} />
        <Stack gap={1} sx={{ pr: 2.5 }}>
          {member.education.map((edu) => (
            <Typography key={edu} variant='body1' sx={{ lineHeight: 1.8 }}>
              {edu}
            </Typography>
          ))}
        </Stack>
      </Stack>

      {/* Certifications */}
      {member.certifications && member.certifications.length > 0 && (
        <Stack gap={2}>
          <SectionHeading title={drawerDict.certifications} barColor={palette.accent} />
          <Stack gap={1} sx={{ pr: 2.5 }}>
            {member.certifications.map((cert) => (
              <Typography key={cert} variant='body1' sx={{ lineHeight: 1.8 }}>
                {cert}
              </Typography>
            ))}
          </Stack>
        </Stack>
      )}

      {/* Back to team */}
      <MuiLink
        component='button'
        onClick={onClose}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          color: palette.secondary,
          textDecoration: 'none',
          cursor: 'pointer',
          alignSelf: 'flex-start',
          mt: 2,
          '&:hover': { color: palette.primary },
        }}
      >
        <ArrowForwardIcon fontSize='small' />
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          {drawerDict.backToTeam}
        </Typography>
      </MuiLink>
    </Stack>
  );
}
